import React from "react";
import { memo } from "react";
import Icon, { IconNames } from "../components/Icon.tsx";
import { SPACINGS } from "../spacings.ts";
import { motion } from "framer-motion";

type Props = {
	/** The label displayed inside the tag */
	label?: string;
	/** Name of the icon to be displayed inside the tag */
	icon?: IconNames;
	/** Size of the tag, affecting padding and text size */
	size?: "sm" | "md" | "lg";
	/** Position of the icon inside the tag */
	iconPosition?: "trailing" | "leading";
	/** Callback function triggered when the tag is clicked */
	onClick?: () => void;
};

const spacing = {
	sm: {
		vertical: 0,
		horizontal: SPACINGS.xs,
	},
	md: {
		vertical: SPACINGS["2xs"],
		horizontal: SPACINGS.sm,
	},
	lg: {
		vertical: SPACINGS["xs"],
		horizontal: SPACINGS.md,
	},
};

function Tag({
	label,
	icon,
	iconPosition = "trailing",
	size = "sm",
	onClick,
}: Props) {
	const tagPadding = spacing[size];

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			whileHover={{
				backgroundColor: "#D0E4FF",
				borderColor: "#0056b3",
				transition: { duration: 0.15 },
			}}
			transition={{ duration: 0.15 }}
			style={{
				display: "inline-flex",
				flexDirection: "row",
				alignItems: "center",
				paddingTop: tagPadding.vertical,
				paddingBottom: tagPadding.vertical,
				paddingLeft: tagPadding.horizontal,
				paddingRight: tagPadding.horizontal,
				backgroundColor: "#F0F6FF",
				borderRadius: 40,
				gap: size === "sm" ? 2 : 4,
				border: "1px solid #2684FF",
				cursor: "pointer",
                position: "relative",
			}}
			onClick={onClick}
		>
			{icon && iconPosition === "leading" && <Icon name={icon} size={size} />}
			{label && (
				<div
					style={{
						maxWidth: 100,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						position: "relative",
					}}
					title={label}
				>
					<p>{label}</p>
					{label.length > 10 && (
						<span
							style={{
								position: "absolute",
								top: "100%",
								left: 0,
								backgroundColor: "#333",
								color: "#fff",
								padding: "4px 8px",
								borderRadius: 4,
								whiteSpace: "nowrap",
								fontSize: "12px",
								visibility: "hidden",
								opacity: 0,
								transition: "opacity 0.2s",
							}}
							className="tooltip"
						>
							{label}
						</span>
					)}
				</div>
			)}
			{icon && iconPosition === "trailing" && <Icon name={icon} size={size} />}
		</motion.div>
	);
}

export default memo(Tag);
